(ns minorodata.components.transformations.modals
  (:require
   [applied-science.js-interop :as js-interop]
   [applied-science.js-interop :as jsi]
   [bleene.dag.api :refer [search-params]]
   [cljs.spec.alpha :as spec]
   [clojure.string :as c.string]
   [lucide-react :refer [AlertCircle AlertTriangle ChevronDown ChevronRight
                         Copy FileEdit FileEdit FileMinus FilePlus Info Trash]]
   [minorodata.app.config :refer [API_HOST]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :as utils]
   [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
   [minorodata.components.monaco.diff-editor :refer [diff-editor]]
   [minorodata.components.monaco.editor :as monaco.editor]
   [minorodata.components.schedule.core :as c.schedule]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.sql-console-modal.utils :refer [close-tab-by-tab-id]]
   [minorodata.components.transformations.groups :refer [fetch-more-groups
                                                         group-list-item
                                                         groups-list
                                                         spinner-list-item]]
   [minorodata.components.transformations.search :refer [search-modal]]
   [minorodata.components.transformations.utils :refer [create-and-edit
                                                        show-transform-settings-modal]]
   [minorodata.group.model :refer [->unsaved-group]]
   [minorodata.sandbox.events :refer [reset-preview]]
   [minorodata.transform.model :refer [->unsaved-transform]]
   [minorodata.utils :refer [find-by-field]]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [react :as react]
   [reagent.core :as reagent]
   [spec-tools.core :as st]
   [ui.accordion :refer [accordion-content accordion-item accordion-root
                         accordion-trigger]]
   [ui.alert :refer [alert]]
   [ui.autocomplete :refer [autocomplete]]
   [ui.button :refer [button]]
   [ui.chip :refer [chip]]
   [ui.date-time :refer [date-tooltip]]
   [ui.dialog :refer [dialog-actions dialog-content dialog-description
                      dialog-root dialog-title]]
   [ui.fields :as fields :refer [input select switch]]
   [ui.table :refer [table tbody td th thead tr]]
   [ui.tabs :as tabs]
   [ui.tooltip :refer [tooltip]]
   [ui.typography :as typography :refer [h2 h4 small]]
   [ui.utils :refer [clsx]]))



(spec/def ::command
  (spec/and (st/spec string? {:reason "Please provide some SQL command"})
            (st/spec (complement c.string/blank?) {:reason "SQL command can't be empty"})))

(spec/def ::group-id
  (st/spec string? {:reason "Please select a group"}))

(spec/def ::cron
  (spec/coll-of string?))

(spec/def ::transform-form
  (spec/and
   (spec/keys :opt-un [::cron])
   (st/spec
    (fn [{:keys [cron]}]
      (or (= utils/empty-cron cron)
          (not (utils/cron-empty-field? cron :minute))))
    {:reason "You should select minutes"})))

(spec/def ::new-transform-form
  (spec/and
   (spec/keys :req-un [::group-id])
   (st/spec
    (fn [{:keys [group-id new-group] :as args}]
      (or (not= group-id "new-group")
          (not (c.string/blank? new-group))))
    {:reason "Provide a new group name or select existing group"})))


(defn span-title [children]
  [:span {:title children} children])

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Create Group

(defn create-new-group [on-success form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:environments.api/create-group {:data       form-data
                                               :on-success on-success}])
    (dispatch [:environments.api/fetch-groups])))

(defn delete-new-group-form []
  (dispatch [:form-model/delete [:form-model/id :new-group-form]]))

(defn create-new-group-render [{:keys [fields errors on-submit]}]
  [:form {:on-submit on-submit
          :class     "flex flex-col gap-4"}
   [fields/input
    {:field         (:group-name fields)
     :errors        errors
     :data-cy       :group-name-input
     :auto-complete "off"
     :variant       "outlined"
     :label         "New group name *"}]

   [fields/input
    {:field         (:group-description fields)
     :errors        errors
     :data-cy       :new-group-description-input
     :textarea      true
     :rows          4
     :auto-complete "off"
     :variant       "outlined"
     :label         "New group description"}]

   [:section {:class "flex justify-end"}
    [button {:data-cy :create-group-button
             :type    "submit"
             :variant :primary}
     "Create group"]]])

(spec/def ::group-name
  (spec/and (st/spec string? {:reason "Group name can not be empty"})
            (st/spec (complement c.string/blank?) {:reason "Group name can not be empty"})))

(spec/def ::new-group-form
  (spec/keys :req-un [::group-name]
             :opt-un [::group-description]))


(defn create-new-group-form [{:keys [on-success]}]
  [form-component/form
   (->form-model
    {:id        :new-group-form
     :validate  (partial form-component/validate-form ::new-group-form)
     :on-submit (partial create-new-group on-success)
     :clean-up  delete-new-group-form
     :fields    [{:name  :group-name
                  :value ""}
                 {:name  :group-description
                  :value ""}]})
   (fn [fields errors on-submit]
     [create-new-group-render
      {:on-submit on-submit
       :errors    errors
       :fields    fields}])])


(defn group-create-dialog [{:keys [open on-close on-success]}]
  [dialog-root {:open           open
                :on-open-change (fn [val]
                                  (when-not val (on-close)))}
   [dialog-content
    [dialog-title "Create new transform group"]
    [:div {:class "mt-4"}
     [create-new-group-form {:on-success on-success}]]]])

(defn group-create-and-move-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        create-group? (:create-group query)
        on-close      #(dispatch [:app.events/navigate-query {:create-group nil :move-transform nil}])
        on-success    (fn [group-id]
                        (on-close)
                        (if-let [id (:move-transform query)]
                          (dispatch [:transform.api/move-to-group {:transform-id id
                                                                   :to-group     group-id
                                                                   :redirect?    false}])
                          (dispatch [:app.events/navigate :transforms {:group-id group-id}])))]
    [group-create-dialog {:open       create-group?
                          :on-close   on-close
                          :on-success on-success}]))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Edit Group

(defn unschedule-group [group-id]
  (dispatch [:group.api/bulk-unschedule-group {:group-id group-id}]))


(defn unschedule-group-confirmation-dialog [{:keys [group-id open? on-close]}]
  (let [on-submit (fn []
                    (unschedule-group group-id)
                    (on-close))]
    [dialog-root {:open open?
                  :on-open-change on-close}
     [dialog-content
      [dialog-title "Unschedule group"]
      [dialog-description
       [:p
        "This action will unschedule all the transforms within this group." [:br]
        [:strong "Are you sure you want to unschedule this group?"]]]

      [dialog-actions
       [button {:on-click  on-submit
                :variant   :primary}
        "Sure"]]]]))


(defn edit-group-render [{:keys [fields errors on-submit changed? group-details default-expanded-schedule?]}]
  (let [timezone     @(re-posh/subscribe [:app.subs/timezone])
        prod-active? @(re-posh/subscribe [:environments.subs/production-active?])

        {:app-route/keys [query]} @(re-posh/subscribe [:app-route/pull])
        show-unschedule?  (:group-unschedule query)
        toggle-unschedule #(dispatch [:app.events/navigate-query {:group-unschedule %}])

        {:keys [id updated-at updated-by created-at created-by schedulable? schedule-tip scheduled?]} group-details]
    [:<>
     [:form {:on-submit on-submit
             :class     "flex flex-col gap-4"}
      [fields/input
       {:field         (:group-name fields)
        :errors        errors
        :id            :group-name
        :auto-complete "off"
        :variant       "outlined"
        :label         "Group name *"
        :controlled?   false}]

      [fields/input
       {:field         (:group-description fields)
        :errors        errors
        :id            :group-description
        :multiline     true
        :rows          3
        :auto-complete "off"
        :variant       "outlined"
        :label         "Group description"
        :data-cy       :group-description-input
        :controlled?   false}]

      [:div {:class "flex flex-col gap-1"}
       [:p "Updated " [date-tooltip {:date updated-at}] " by " updated-by]
       [:p "Created " [date-tooltip {:date created-at}] " by " created-by]]

      [accordion-root {:collapsible   true
                       :default-value (when default-expanded-schedule? "schedule")
                       :data-cy       "schedule-accordion"}
       [accordion-item {:value    "schedule"
                        :disabled (or (not schedulable?) (not prod-active?))}
        [accordion-trigger
         [:div {:class "flex items-center justify-between"}
          [:div "Schedule (" timezone " time zone): "

           (if (not schedulable?)
             [:span {:class "text-kleene-secondary-v2"}
              schedule-tip]

             [tooltip {:content schedule-tip}
              [:span {:class "text-kleene-secondary-v2"}
               (utils/format-schedule
                {:cron            (get-in fields [:cron :value])
                 :empty-status    "Not scheduled"
                 :required-fields [:minute]})]])]

          (when scheduled?
            [button {:variant :empty
                     :size    :small
                     :on-click (fn [e]
                                 (.stopPropagation e)
                                 (.preventDefault e)
                                 (toggle-unschedule true))}
             "Unschedule"])]]
        [accordion-content
         [:div {:class "max-w-2xl my-4 mx-auto"}
          [c.schedule/cron-editor {:field (:cron fields)}]]]]]

      (when (some? (:form errors))
        [:p {:class "text-kleene-text-error"}
         (-> errors :form :value)])

      [:section {:class "flex justify-end"}
       [button {:type     "submit"
                :disabled (and (not changed?) (not (some? (:form errors))))
                :variant  :primary
                :data-cy  :group-updates-submit}
        "Save changes"]]]

     [unschedule-group-confirmation-dialog
      {:group-id id
       :open?    show-unschedule?
       :on-close #(toggle-unschedule nil)}]]))

(spec/def ::cron
  (spec/nilable string?))

(spec/def ::edit-group-form
  (spec/and
   (spec/keys :req-un [::group-name ::cron]
              :opt-un [::group-description])
   (st/spec
    (fn [{:keys [cron]}]
      (let [cron-map (utils/cron->map cron)]
        (or (= utils/empty-cron cron)
            (not (utils/cron-empty-field? cron-map :minute)))))
    {:reason "You should select minutes"})))

(defn update-group [group-id on-success form-state]
  (let [form-data (form-component/model->fields form-state)
        {:keys [group-name group-description cron]} form-data
        has-cron? (not= cron utils/empty-cron)
        group     (utils/assoc-some
                   {:id          group-id
                    :name        group-name
                    :description group-description}
                   :schedule-spec (when has-cron? {:cron cron}))]
    (dispatch [:environments.api/update-group {:data       group
                                               :on-success on-success}])))


(defn add-unsaved-group? [group-id form-state]
  (when (:form-model/changed form-state)
    (let [form          (form-component/model->fields form-state)
          unsaved-group {:id          group-id
                         :name        (:group-name form)
                         :description (:group-description form)
                         :cron        (:cron form)}]
      (dispatch [:unsaved-group/add (->unsaved-group unsaved-group)]))))


(defn delete-edit-group-form [group-id form-state]
  (add-unsaved-group? group-id form-state)
  (dispatch [:form-model/delete [:form-model/id :edit-group-form]]))


(defn edit-group [{:keys [group-id]}]
  (let [loading-schedule? (re-posh/subscribe [:mold-http/loading? :group.api/fetch-schedule])]
    (reagent/create-class
     {:display-name
      "edit-group"

      :constructor
      (fn []
        (dispatch-sync [:group.api/fetch-schedule {:group-id group-id}]))

      :get-snapshot-before-update
      (fn [this [_ prev-props] _]
        (let [props      (reagent/props this)
              form-state @(re-posh/subscribe [:form-model/pull [:form-model/id :edit-group-form]])]
          (when-not (= (:group-id prev-props) (:group-id props))
            (add-unsaved-group? (:group-id prev-props) form-state))))

      :component-did-update
      (fn [])

      :render
      (fn [this]
        (let [{:keys [group-id on-success]} (-> this reagent/props)
              panel-group @(re-posh/subscribe [:group.subs/panel-group-details group-id])
              {:keys [name description changed?]} panel-group
              {:app-route/keys [query]} @(re-posh/subscribe [:app-route/pull])
              default-expanded-schedule? (:edit-schedule query)
              cron (utils/->cron panel-group)]

          (if @loading-schedule?
            [:div {:class "m-6"}
             [c.spinner/spinner-lucide]]

            [form-component/form
             (->form-model
              {:id            :edit-group-form
               :validate      (partial form-component/validate-form ::edit-group-form)
               :on-submit     (partial update-group group-id on-success)
               :clean-up      (partial delete-edit-group-form group-id)
               :track-changes true
               :changed       changed?
               :fields        (cond-> [{:name :group-name :value name}
                                       {:name :group-description :value description}
                                       {:name :cron :value (or cron utils/empty-cron) :process identity}])})
             (fn [fields errors on-submit {:keys [changed?]}]
               ^{:key cron}
               [edit-group-render
                {:on-submit                  on-submit
                 :errors                     errors
                 :fields                     fields
                 :changed?                   changed?
                 :group-details              panel-group
                 :default-expanded-schedule? default-expanded-schedule?}])])))})))

(defn group-edit-dialog [{:keys [route]}]
  (let [{:app-route/keys [name params query]} route
        open? (:edit-group query)
        group-id (or (:group-id params) (:group-id query))
        group @(re-posh/subscribe [:group.subs/panel-group-details group-id])

        on-close (fn []
                   (dispatch [:app.events/navigate-query {:edit-group nil
                                                          :group-id   nil}]))]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open
                                      (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true}
       "Edit group '" [span-title (:name group)] "'"]
      [:div {:class "mt-4"}
       [edit-group {:group-id   group-id
                    :on-success (fn []
                                  (when (= :dag name) (dispatch [:dag/load-dag-data]))
                                  (on-close))}]]]]))

(defn delete-group [group-id]
  (let [on-success #(dispatch [:app.events/navigate-query {:delete-group nil}])]
    (dispatch [:environments.api/delete-group {:group-id   group-id
                                               :navigate?  false
                                               :on-success on-success}])))

(defn group-delete-dialog [{:keys [route]}]
  (let [{:app-route/keys [params query]} route
        {:keys [group-id]} params
        open? (:delete-group query)

        group    @(re-posh/subscribe [:group.subs/panel-group-details group-id])
        on-close #(dispatch [:app.events/navigate-query {:delete-group nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content
      [dialog-title {:truncate true}
       "Delete group '" [span-title (:name group)] "'?"]
      [dialog-description
       "Are you sure you want to delete this group?" [:br]
       [:strong {:class "font-bold"} "All associated transforms will be deleted too."]]
      [dialog-actions
       [button {:variant  :primary
                :data-cy  "group-delete-confirm-btn"
                :on-click #(delete-group group-id)}
        "Delete group"]]]]))


;; ================
;; Transforms

(defn delete-transform [group-id transform-id tab-id pipeline-editor]
  (let [on-success
        (fn []
          (when tab-id
            (close-tab-by-tab-id tab-id))

          (dispatch [:app.events/navigate-query (cond-> {:delete-transform nil
                                                         :transform-id     nil
                                                         :group-id         nil}
                                                  pipeline-editor
                                                  (merge reset-preview))])

          (when pipeline-editor
            (when (= @(re-posh/subscribe [:pipeline-editor.subs/active-tab-id])
                     transform-id)
              (dispatch [:pipeline-editor.events/close-tab transform-id]))
            (dispatch [:dag.api/fetch-graph])
            (dispatch [:dag.api/load-dag-metadata search-params])))]
    (dispatch [:environments.api/delete-transform {:group-id     group-id
                                                   :transform-id transform-id
                                                   :navigate?    false
                                                   :on-success   on-success}])))

(defn transform-delete-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {:keys [pipeline-editor transform-id group-id tab-id]} query
        open? (:delete-transform query)

        transform (or @(re-posh/subscribe [:transform.subs/panel-transform transform-id])
                      (-> @(re-posh/subscribe [:sandbox-transform/pull [:sandbox-transform/master-id transform-id]])
                          utils/drop-ns-in-keys))
        sandbox?  @(re-posh/subscribe [:sandbox.subs/enabled?])
        on-close  (fn [& [close-preview?]]
                    (dispatch [:app.events/navigate-query
                               (merge {:delete-transform nil
                                       :group-id         nil
                                       :transform-id     nil
                                       :pipeline-editor  nil}
                                      (when close-preview? reset-preview))]))]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content
      [dialog-title {:truncate true}
       "Delete transform '" [span-title (:name transform)] "'?"]
      [dialog-description "Are you sure you want to delete this transform?"]
      [:div {:class "mt-4 flex justify-end"}
       [button {:on-click  #(if (and pipeline-editor sandbox?)
                              (do
                                (dispatch [:sandbox.events/delete-sandbox-transform {:id transform-id}])
                                (on-close true))
                              (delete-transform group-id transform-id tab-id pipeline-editor))
                :data-cy   "transform-delete-modal-confirm-btn"
                :variant   :error}
        "Delete transform"]]]]))

(defn add-unsaved-transform [transform-id form-state]
  (let [{:keys [cron] :as form-data} (form-component/model->fields form-state)
        has-schedule? (not= utils/empty-cron cron)
        transform     (-> form-data
                          (dissoc :cron)
                          (utils/assoc-some :id transform-id
                                            :schedule-spec (when has-schedule? {:cron cron})))
        transform-model (->unsaved-transform transform)]
    (dispatch [:unsaved-transform/add transform-model])
    transform))

(defn update-transform [group-id transform-id form-state]
  (let [transform  (add-unsaved-transform transform-id form-state)
        on-success (fn []
                     (let [{:app-route/keys [name]} @(re-posh/subscribe [:app-route/pull])]
                       (when (#{:dag :pipeline-editor} name)
                         (dispatch [:dag/load-dag-data]))
                       (when (not= :pipeline-editor name)
                         (dispatch [:transform.events/refresh-panel])))

                     (dispatch [:app.events/navigate-query {:transform-settings nil
                                                            :group-id           nil
                                                            :transform-id       nil}]))]
    (dispatch [:environments.api/update-transform {:group-id     group-id
                                                   :transform    transform
                                                   :upsert-event :transform.api/update-transform
                                                   :on-success   on-success}])))

(defn clone-transform [group-id form-state]
  (let [transform  (add-unsaved-transform nil form-state)
        on-success (fn []
                     (let [{:app-route/keys [name]} @(re-posh/subscribe [:app-route/pull])]
                       (when (= :dag name)
                         (dispatch [:dag/load-dag-data])))

                     (dispatch [:transform.events/refresh-panel])
                     (dispatch [:app.events/navigate-query {:clone-transform    nil
                                                            :clone-transform-id nil
                                                            :clone-group-id     nil}]))]
    (dispatch [:environments.api/create-transform {:group-id   group-id
                                                   :transform  transform
                                                   :on-success on-success}])))

(defn go-to-group-edit [group-id]
  (dispatch [:app.events/navigate :transforms {:group-id group-id} {:edit-group true}]))

(defn deps-input-render [{:keys [label values disabled on-change]}]
  [:div
   [:form {:on-submit (fn [event]
                        (js-interop/call event :preventDefault)
                        (let [input     (js-interop/get-in event [:target :elements :deps-input])
                              new-value (utils/trim? (js-interop/get input :value))]
                          (when-not (empty? new-value)
                            (on-change (conj values new-value))
                            (js-interop/assoc! input :value ""))))}
    [:div {:class "flex gap-2 items-end"}
     [fields/input {:id     "deps-input"
                    :name   "deps-input"
                    :label  label
                    :disabled  disabled
                    :size   "small"}]
     [button {:type     :submit
              :variant  :empty
              :disabled disabled}
      "Add"]]]

   (into
    [:div {:class "flex gap-1 flex-wrap mt-1"}]
    (for [v values]
      ^{:key v}
      [chip {:on-delete (when-not disabled
                          #(on-change (utils/remove-elem values v)))}
       v]))])

(defn dependency-list [items & [on-delete]]
  (into
   [:<>]
   (for [value items]
     ^{:key value}
     [chip {:on-delete (when on-delete #(on-delete value))}
      value])))

(defn tags-autocomplete []
  (let [tr-view (re-posh/subscribe [:transformations-view/pull
                                    '[{:transformations-view/transforms-tags [*]}]])]
    (fn [{:keys [tags on-change]}]
      [:<>
       [autocomplete {:label     "Add tags"
                      :values    (or tags [])
                      :options   (or (->> (:transformations-view/transforms-tags @tr-view)
                                          (mapv (fn [{:transform-tag/keys [value label]}]
                                                  {:value value :text label})))
                                     [])
                      :on-change (fn [value]
                                   (on-change (mapv #(if (string? %) % (js-interop/get % :value))
                                                    value)))}]])))

(defn unschedule-transform [group-id transform-id]
  (let [form-state @(re-posh/subscribe [:form-model/pull [:form-model/id :edit-transform-form]])

        redirect-to-new-transform (fn [{:keys [transform]}]
                                    (show-transform-settings-modal transform))

        transform  (-> form-state
                       form-component/model->fields
                       (dissoc :cron)
                       (assoc :id transform-id))]
    (dispatch [:environments.api/unschedule-transform {:group-id  group-id
                                                       :transform transform
                                                       :on-success redirect-to-new-transform}])))


(defn unschedule-transform-confirmation-dialog [{:keys [group-id transform-id open? on-close]}]
  (let [on-submit (fn []
                    (unschedule-transform group-id transform-id)
                    (on-close))]
    [dialog-root {:open open?
                  :on-open-change on-close}
     [dialog-content
      [dialog-title "Unschedule transform"]
      [dialog-description
       [:p
        "This action will unschedule this transforms." [:br]
        [:strong "Are you sure you want to proceed?"]]]

      [:div {:class "mt-4 flex justify-end"}
       [button {:on-click  on-submit
                :variant   :primary}
        "Sure"]]]]))

(defn transform-settings-fields
  [{:keys [fields errors show-deps? scheduled?
           group-id transform-id group-bulk-scheduled? upstream-deps? dev-env? hide-schedule?]
    :or   {show-deps? false}}]
  (let [{:keys [status is-full ignore-parser
                input-tables output-tables name description tags]} fields

        timezone     @(re-posh/subscribe [:app.subs/timezone])
        prod-active? @(re-posh/subscribe [:environments.subs/production-active?])
        group        @(re-posh/subscribe [:group/pull [:group/id group-id]])

        active?        (= :active (:value status))
        is-full?       (:value is-full)
        ignore-parser? (:value ignore-parser)
        disabled       dev-env?

        [unschedule-modal-open? set-unschedule-modal] (react/useState false)]

    [:<>
     [:div {:class "flex flex-col gap-4"}

      (when-not prod-active?
        [alert {:variant :warning
                :icon    AlertTriangle
                :title   "Some fields are disabled in development environment."}])

      [:div {:class "grid grid-cols-2 gap-4"}
       [switch {:label     "Active"
                :checked   active?
                :disabled  disabled
                :on-change #((:action status) (if active? :paused :active))}]
       [switch {:label     "Replace existing table"
                :checked   is-full?
                :disabled  disabled
                :on-change #((:action is-full) (not is-full?))}]
       [switch {:label     "Specify input and output tables manually"
                :checked   ignore-parser?
                :disabled  disabled
                :on-change #((:action ignore-parser) (not ignore-parser?))}]]

      (when (and show-deps? (not ignore-parser?))
        [:div
         [h4 "Transform dependencies"]

         [:div {:class "mt-1 flex gap-x-2"}
          [:div {:class "shrink-0 leading-5"} "Input tables:"]
          [:div {:class "flex flex-wrap gap-x-2 gap-y-0.5"}
           (if (empty? (:value input-tables))
             [small {:class "leading-5"} "-"]
             [dependency-list (:value input-tables)])]]

         [:div {:class "mt-1 flex gap-x-2"}
          [:div {:class "shrink-0 leading-5"} "Output tables: "]
          [:div {:class "flex flex-wrap gap-x-2 gap-y-0.5"}
           (if (empty? (:value output-tables))
             [small {:class "leading-5"} "-"]
             [dependency-list (:value output-tables)])]]])


      (when ignore-parser?
        [:<>
         [deps-input-render {:label     "Add input table"
                             :values    (:value input-tables)
                             :disabled  disabled
                             :on-change (:action input-tables)}]

         [deps-input-render {:label     "Add output table"
                             :values    (:value output-tables)
                             :disabled  disabled
                             :on-change (:action output-tables)}]])

      [fields/input
       {:field         name
        :errors        errors
        :controlled?   false
        :id            :transform-name
        :data-cy       :transform-name-input
        :label         "Transform name"}]

      [fields/input
       {:field         description
        :errors        errors
        :id            :transform-description
        :textarea      true
        :label         "Transform description"}]

      [tags-autocomplete
       {:tags      (:value tags)
        :on-change (:action tags)}]

      (when-not hide-schedule?
        (if (and group-bulk-scheduled?
                 (not upstream-deps?))
          [:div {:class "my-4 flex items-center justify-between"}
           [:div
            "This transform is using a group schedule: "
            [:strong {:class "font-semibold"}
             (utils/format-schedule {:cron (get-in group [:group/schedule-spec :cron])})]]

           [:div {:class "shrink-0"}
            [button {:variant  :secondary
                     :data-cy  "edit-group-schedule-redirect-btn"
                     :on-click #(go-to-group-edit group-id)}
             "Edit group schedule"]]]

          [accordion-root {:collapsible (not disabled)
                           :disabled    disabled
                           :data-cy     "show-schedule-details"}
           [accordion-item {:value    "schedule"}
            [accordion-trigger {:data-cy "transform-schedule-edit-btn"}
             [:div {:class   "flex items-center justify-between"}
              [:div
               "Schedule (" timezone " time zone): "
               [:strong {:class "font-semibold"}
                (utils/format-schedule
                 {:cron            (get-in fields [:cron :value])
                  :empty-status    (if upstream-deps?
                                     "Dependency based"
                                     "Not scheduled")
                  :required-fields [:minute]})]]

              (when scheduled?
                [button {:variant :empty
                         :size    :small
                         :on-click (fn [e]
                                     (.stopPropagation e)
                                     (.preventDefault e)
                                     (set-unschedule-modal true))}
                 "Unschedule"])]]

            [accordion-content
             [:div {:class "max-w-2xl my-4 mx-auto"}
              [c.schedule/cron-editor
               {:field     (:cron fields)
                :disabled? group-bulk-scheduled?}]]]]]))

      (when (some? (:form errors))
        [:span {:class "text-kleene-text-error"}
         (-> errors :form :value)])]

     [unschedule-transform-confirmation-dialog
      {:open?        unschedule-modal-open?
       :group-id     group-id
       :transform-id transform-id
       :on-close     #(set-unschedule-modal false)}]]))

(defn delete-edit-transform-form [transform-id form-state]
  (when (:form-model/changed form-state)
    (add-unsaved-transform transform-id form-state))

  (dispatch [:form-model/delete [:form-model/id :edit-transform-form]]))

(defn transform-settings [{:keys [transform]}]
  (let [{:keys [group-id id transform-id]} transform
        transform-id (or transform-id id)
        dev-env?  (not @(re-posh/subscribe [:environments.subs/production-active?]))
        group     @(re-posh/subscribe [:group/pull [:group/id group-id]])

        loading? (or @(re-posh/subscribe [:mold-http/loading? :transform.api/parse-sql-v2])
                     @(re-posh/subscribe [:mold-http/loading? :transform.api/update-transform])
                     @(re-posh/subscribe [:mold-http/loading? :environments.api/update-environment-transform]))

        cron (utils/->cron transform)]
    [:div {:class "mt-4"}
     [form-component/form
      (->form-model
       {:id            :edit-transform-form
        :track-changes true
        :changed       (boolean (:changed? transform))
        :validate      (partial form-component/validate-form ::transform-form)
        :on-submit     (partial update-transform group-id transform-id)
        :clean-up      (partial delete-edit-transform-form transform-id)
        :fields        [{:name :status :value (:status transform) :process identity}
                        {:name :is-temp :value false :process identity} ; kioku expects is_temp, hardcode to false
                        {:name :is-full :value (:is-full transform) :process identity}
                        {:name :ignore-parser :value (:ignore-parser transform) :process identity}
                        {:name :input-tables :value (:input-tables transform) :process identity}
                        {:name :output-tables :value (:output-tables transform) :process identity}
                        {:name :command :value (:command transform) :process identity}
                        {:name :name :value (:name transform)}
                        {:name :description :value (:description transform)}
                        {:name :tags :value (:tags transform) :process identity}
                        {:name :cron :value cron :process identity}]})
      (fn [fields errors on-submit]
        [:<>
         [:f> transform-settings-fields
          {:on-submit             on-submit
           :errors                errors
           :fields                fields
           :group-id              group-id
           :transform-id          transform-id
           :scheduled?            (:scheduled? transform)
           :show-deps?            true
           :group-bulk-scheduled? (:group/bulk-scheduled group)
           :dev-env?              dev-env?}]

         [:footer {:class "mt-4 flex justify-end"}
          [button {:data-cy  "update-transform-btn"
                   :disabled loading?
                   :on-click on-submit}
           "Save"]]])]]))

(defn transform-settings-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {:keys [transform-id]} query
        open?     (:transform-settings query)
        transform @(re-posh/subscribe [:transform.subs/edited-transform transform-id])
        on-close  #(dispatch [:app.events/navigate-query {:transform-settings nil
                                                          :group-id           nil
                                                          :transform-id       nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true}
       "Edit transform '" [span-title (:name transform)] "'"]

      [transform-settings {:transform transform}]]]))

(defn transform-clone-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {transform-id :clone-transform-id
         group-id     :clone-group-id} query

        open?     (:clone-transform query)

        dev-env?  (not @(re-posh/subscribe [:environments.subs/production-active?]))
        transform @(re-posh/subscribe [:transform.subs/edited-transform transform-id])
        group     @(re-posh/subscribe [:group/pull [:group/id group-id]])

        loading?  (or @(re-posh/subscribe [:mold-http/loading? :transform.api/parse-sql-v2])
                      @(re-posh/subscribe [:mold-http/loading? :transform.api/update-transform])
                      @(re-posh/subscribe [:mold-http/loading? :environments.api/update-environment-transform]))

        on-close  #(dispatch [:app.events/navigate-query {:clone-transform    nil
                                                          :clone-group-id     nil
                                                          :clone-transform-id nil}])
        cron      (utils/->cron transform)]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true}
       "Clone transform '" [span-title (:name transform)] "'"]

      [:div {:class "mt-4"}
       [form-component/form
        (->form-model
         {:id            :clone-transform-form
          :track-changes true
          :changed       (boolean (:changed? transform))
          :validate      (partial form-component/validate-form ::transform-form)
          :on-submit     (partial clone-transform group-id)
          :clean-up      #(dispatch [:form-model/delete [:form-model/id :clone-transform-form]])
          :fields        [{:name :status :value (:status transform) :process identity}
                          {:name :is-temp :value false :process identity} ; kioku expects is_temp, hardcode to false
                          {:name :is-full :value (:is-full transform) :process identity}
                          {:name :ignore-parser :value (:ignore-parser transform) :process identity}
                          {:name :input-tables :value (:input-tables transform) :process identity}
                          {:name :output-tables :value (:output-tables transform) :process identity}
                          {:name :command :value (:command transform) :process identity}
                          {:name :name :value (str "CLONE - " (:name transform))}
                          {:name :description :value (:description transform)}
                          {:name :tags :value (:tags transform) :process identity}]})
        (fn [fields errors on-submit]
          [:<>
           [:f> transform-settings-fields
            {:on-submit             on-submit
             :errors                errors
             :fields                fields
             :group-id              group-id
             :transform-id          transform-id
             :hide-schedule?        true
             :show-deps?            true
             :dev-env?              dev-env?}]

           [:footer {:class "mt-4 flex justify-end"}
            [button {:data-cy  "update-transform-btn"
                     :disabled loading?
                     :on-click on-submit}
             "Create transform"]]])]]]]))


;; ==================
;; Environments

(def env-colors ["#59A14F", "#EDC948", "#F28E2B"])

(defn create-environment-render [{:keys [on-submit errors fields]}]
  (let [loading? @(re-posh/subscribe [:mold-http/loading? :environments.api/create-environment])]
    [:div
     [:div {:class "flex flex-col gap-2"}
      [fields/input
       {:field         (:name fields)
        :errors        errors
        :id            :name
        :data-cy       :create-env-name-input
        :auto-complete "off"
        :variant       "outlined"
        :label         "Name *"}]
      [:div
       [typography/label "Colour *"]
       [:div
        [:div {:class "border rounded-xs inline-flex gap-2 p-2"}
         (for [color env-colors]
           ^{:key color}
           [:button {:class    (clsx "rounded-full h-4 w-4"
                                     {"ring-2" (= (-> fields :colour :value) color)})
                     :style    {:background color}
                     :on-click #((-> fields :colour :action) color)}])]
        (when (get errors :colour)
          [:div {:class "mt-1 text-xs text-kleene-error-01"} (get-in errors [:colour :value])])]]]
     [:footer {:class "mt-4 flex justify-end"}
      [:div {:class "flex flex-col gap-2 items-end"}
       [button {:on-click (fn [evt]
                            ((:action (:open-modal fields)) true)

                            (js/setTimeout
                             (fn []
                               (on-submit evt))
                             10))
                :loading  (and loading? (get-in fields [:open-modal :value]))
                :disabled loading?}
        "Set up with data"]

       [button {:on-click (fn [evt]
                            ((:action (:open-modal fields)) false)
                            (js/setTimeout
                             (fn []
                               (on-submit evt))
                             10))
                :variant  :secondary
                :loading  (and loading? (not (get-in fields [:open-modal :value])))
                :disabled loading?}
        "Set up with no data"]]]]))

(defn create-environment [on-success _]
  ;; Need to access the form data directly, since form-state provided in the
  ;; callback function is already stale at this point in time
  (let [form-state @(re-posh/subscribe [:form-model/pull [:form-model/id :create-environment-form]])
        form-data (form-component/model->fields form-state)]
    (dispatch [:environments.api/create-environment
               {:data       (dissoc form-data :open-modal)
                :with-data? (:open-modal form-data)
                :on-success (fn [environment]
                              (on-success environment)

                              (dispatch [:environments.events/select-env (:environment/environment-id environment)])

                              (when (:open-modal form-data)
                                (dispatch [:sql-console-modal.events/set-db-copy-env (:environment/environment-id environment)])
                                (dispatch [:sql-console-modal.events/toggle-db-copy-modal true])))}])))


(defn valid-name?
  [input]
  (re-matches #"^[a-zA-Z0-9_]+$" input))

(spec/def ::name
  (spec/and (st/spec string? {:reason "Name can not be empty"})
            (st/spec (complement c.string/blank?) {:reason "Name can not be empty"})
            (st/spec valid-name? {:reason "A-Z, a-z, numbers and underscores only"})))

(spec/def ::colour
  (spec/and (st/spec string? {:reason "Colour must be picked"})
            (st/spec (complement c.string/blank?) {:reason "Colour must be picked"})))

(spec/def ::create-environment-form
  (spec/keys :req-un [::name ::colour]))

(defn create-environment-dialog-render [{:keys [on-close]}]
  [dialog-content
   [dialog-title "Create environment"]
   [dialog-description "This action will copy all production transforms and create a restore point. It can take up to 15 seconds."]
   [:div {:class "mt-4"}
    [form-component/form
     (->form-model
      {:id            :create-environment-form
       :validate      (partial form-component/validate-form ::create-environment-form)
       :on-submit     (partial create-environment on-close)
       :fields        [{:name :name :value ""}
                       {:name :open-modal :value false :process identity}
                       {:name :colour :value "" :process identity}]})
     (fn [fields errors on-submit]
       [create-environment-render
        {:on-submit on-submit
         :errors    errors
         :fields    fields}])]]])

(defn environment-create-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route

        open?    (:create-environment query)
        on-close #(dispatch [:app.events/navigate-query {:create-environment nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [create-environment-dialog-render {:on-close on-close}]]))

(defn delete-environment [environment on-close]
  (let [on-success (fn []
                     (on-close)
                     (dispatch [:environments.events/select-env utils/production-env-id]))]
    (dispatch [:environments.api/delete-environment {:environment-id (:environment/environment-id environment)
                                                     :on-success     on-success}])))

(defn environment-delete-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        open? (:delete-environment query)

        active-environment @(re-posh/subscribe [:environments.subs/active-environment])

        on-close #(dispatch [:app.events/navigate-query {:delete-environment nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content
      [dialog-title {:truncate true}
       "Delete environment '" [span-title (:environment/name active-environment)] "'?"]
      [:div {:class "mt-4"}
       [:p "Are you sure you want to delete this environment?"]
       [:p [:strong {:class "font-bold"} "All associated transforms will be deleted too."]]
       [:div {:class "mt-4 flex justify-end"}
        [button {:on-click  #(delete-environment active-environment on-close)
                 :variant   :primary}
         "Delete environment"]]]]]))


;; ==================
;; Restore

(defn restore-production-render [{:keys [on-submit errors fields environments]}]
  [:div {}
   [:div {}
    [fields/radio-root {:field    (get fields :environment-id)
                        :id       :environment-id
                        :required true}
     [table
      [thead
       [tr
        [th {:class "w-6"}]
        [th "Environment"]
        [th "Created at"]]]
      [tbody
       (for [{:environment/keys [environment-id created-at name]} environments]
         ^{:key environment-id}
         [tr
          [td {:class "w-6"}
           [fields/radio-item
            {:id     :environment-id
             :value  environment-id
             :errors errors}]]
          [td name]
          [td
           [date-tooltip {:date created-at}]]])]]]]

   [:div {:class "mt-4 flex justify-end"}
    [button {:on-click  on-submit
             :variant   :primary
             :disabled  (empty? environments)}
     "Restore"]]])


(defn delete-restore-environment-form []
  (dispatch [:form-model/delete [:form-model/id :create-environment-form]]))

(defn restore-environment [form-state]
  (let [fields (form-component/model->fields form-state)
        environment-id (:environment-id fields)]
    (dispatch [:environments.api/restore-environment {:environment-id environment-id}])))

(defn restore-dialog-form []
  (let [{:environments-panel/keys [environments]}
        @(re-posh/subscribe [:environments-panel/pull '[{:environments-panel/environments [*]}]])]
    [form-component/form
     (->form-model
      {:id            :restore-environment-form
       :on-submit     restore-environment
       :clean-up      delete-restore-environment-form
       :fields        [{:name :environment-id :value false :process identity}]})
     (fn [fields errors on-submit]
       [restore-production-render
        {:on-submit    on-submit
         :errors       errors
         :fields       fields
         :environments environments}])]))

(defn restore-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route

        open?        (:restore query)
        on-close     #(dispatch [:app.events/navigate-query {:restore nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content
      [dialog-title [:> AlertTriangle {:class "mr-2 align-middle"}] "Restore Production"]
      [dialog-description {:class "ml-8"} "Warning - This action will restore all production transforms with a previously saved version."]
      [:div {:class "mt-4"}
       [restore-dialog-form]]]]))

(def modification-icons
  {"deleted"           FileMinus
   "directly-modified" FileEdit
   "newly-created"     FilePlus})

(def modification-colors
  {"deleted"           "text-kleene-error-02"
   "directly-modified" "text-kleene-warning-02"
   "newly-created"     "text-kleene-primary-02"})

(defn diff-row [{:keys [transform selectable?]}]
  (dispatch [:environments.api/fetch-transform-diff {:transform-id (:transform/id transform)
                                                     :group-id     (:transform/group-id transform)}])
  (fn [{:keys [transform]}]
    (let [{:transform/keys [id modification-state]} transform
          loading? @(re-posh/subscribe [:mold-http/loading? :environments.api/fetch-transform-diff])
          deleted? (= modification-state "deleted")
          created? (= modification-state "created")

          {:transform-diff/keys [master-transform-sql environment-transform-sql]}
          @(re-posh/subscribe [:transform-diff/pull [:transform-diff/transform-id id]])]
      [tr
       [td {:col-span   (if selectable? 6 5)
            :no-padding true}
        [:div {:class "h-[460px]"}
         (if loading?
           [c.spinner/spinner-lucide]
           [:f> diff-editor
            {:original       (if created? "" master-transform-sql)
             :modified       (if deleted? "" environment-transform-sql)
             :layout-timeout 100}])]]])))

(defn summary-table-row []
  (let [opened?  (reagent/atom false)]
    (fn [{:keys [transform selectable? selected? on-select on-edit]}]
      (let [{:transform/keys [id name modification-state updated-at updated-by]} transform
            icon (get modification-icons modification-state)]
        [:<>
         [tr
          [td {:class "w-4"}
           [:div {:class    "text-kleene-secondary-v2 hover:text-kleene-secondary-v2/60 transition-all cursor-pointer leading-zero"
                  :on-click #(swap! opened? not)}
            [:> ChevronRight {:class (clsx {"rotate-90" @opened?})}]]]
          [td {:no-padding true :class "w-4"}
           [:div {:class (clsx "leading-zero"
                               (get modification-colors modification-state))}
            (when icon
              [:> icon {:style {:line-height 0}}])]]
          [td name]
          [td [date-tooltip {:date updated-at}] [:br] " by " (:searchable-user/username updated-by)]
          [td {:class "text-right"}
           [:span {:class    "underline cursor-pointer"
                   :on-click #(on-edit id)}
            "Edit"]]
          (when selectable?
            [td {:class "text-right"}
             [fields/checkbox {:checked   selected?
                               :on-change #(on-select id)
                               :classes   {:root "inline-block"}}]])]

         (when @opened?
           [diff-row {:transform   transform
                      :selectable? selectable?}])]))))

(defn summary-table [{:keys [transforms selectable? on-select selected-ids on-edit]}]
  [table
   [thead
    [tr
     [th {:class "w-6"}]
     [th {:class "w-6"}]
     [th {:class "w-1/2"} "Name"]
     [th {:class "w-[200px]"} "Updated"]
     [th {:class "text-right"} "Actions"]
     (when selectable?
       [th {:class "text-right"} "Merge"])]]
   [tbody
    (if (empty? transforms)
      [tr [td {:col-span 6} "No transforms found."]]
      (for [{:transform/keys [id] :as t} transforms]
        (let [selected? (some #(= % id) selected-ids)]
          ^{:key (str id (if selected? "on" "off"))}
          [summary-table-row {:transform   t
                              :selected?   selected?
                              :selectable? selectable?
                              :on-select   on-select
                              :on-edit     on-edit}])))]])

(defn merge-environment []
  (let [ids @(re-posh/subscribe [:environments-panel/prop :environments-panel/merge-transform-ids])
        on-success (fn []
                     (dispatch [:app.events/navigate-query {:compare nil :group-id nil}])
                     (dispatch [:environments.events/select-env utils/production-env-id]))]
    (dispatch [:environments.api/merge-environment {:transform-ids ids
                                                    :on-success    on-success}])))

(defn modified-transform? [{:transform/keys [modification-state]}]
  (not= modification-state "unmodified"))

(defn modified-transforms [{:keys [group-id selectable? on-edit]}]
  (let [{:group-transforms/keys [transforms]}
        @(re-posh/subscribe [:group-transforms/pull [:group-transforms/id group-id] '[{:group-transforms/transforms [* {:transform/updated-by [*]}]}]])
        transforms (filter modified-transform? transforms)

        ids @(re-posh/subscribe [:environments-panel/prop :environments-panel/merge-transform-ids])
        on-select #(dispatch [:environments.events/toggle-merge-transform-id %])]
    [:div {:class "flex flex-col overflow-hidden"}
     [:header {:class "px-5 py-4 border-b bg-kleene-background"}
      [h2 "Transforms"]]
     [:div {:class "overflow-y-auto"}
      ^{:key group-id}
      [summary-table {:transforms   transforms
                      :on-edit      on-edit
                      :selectable?  selectable?
                      :selected-ids ids
                      :on-select    on-select}]]]))

(defn refetch-environment []
  (let [env-id @(re-posh/subscribe [:environments.subs/active-environment-id])]
    (dispatch [:environments.api/fetch-environment {:environment-id env-id}])))

(defn diffs-dialog-render []
  (refetch-environment)

  (fn [{:keys [group-id mergable? on-close]}]
    (let [active-env   @(re-posh/subscribe [:environments.subs/active-environment])
          groups       @(re-posh/subscribe [:environments.subs/modified-groups])
          loading?     @(re-posh/subscribe [:mold-http/loading? :environments.api/fetch-environment])
          selected-ids @(re-posh/subscribe [:environments-panel/prop :environments-panel/merge-transform-ids])

          on-edit  (fn [id]
                     (when on-close (on-close))
                     (dispatch [:sql-console-modal.events/open-transform id]))]

      [:<>
       [:header {:class "relative border-b px-5 py-4"}
        [:div {:class "absolute inset-0 opacity-40"
               :style {:background (:environment/colour active-env)}}]

        [:div {:class "relative z-10"}
         [dialog-title {:trancate true}
          "Changes between 'Production' and '" [span-title (:environment/name active-env)] "'"]]]
       [:div {:class "grid grid-cols-[200px_1fr] 2xl:grid-cols-[400px_1fr] flex-1 overflow-hidden"}
        [:div {:class "border-r flex flex-col overflow-hidden"}
         [:header {:class "shrink-0 border-b px-5 py-4 flex justify-between items-center bg-kleene-background"}
          [h2 "Groups"]]

         [:div {:class "overflow-y-auto"}
          (if (and (not loading?) (empty? groups))
            [:div {:class "p-4"} "No modified groups found."]
            (into
             [c.infinite-scroll/main
              {:component       groups-list
               :loader          [spinner-list-item]
               :loading?        loading?
               :on-scroll-end   fetch-more-groups}]
             (for [{:group/keys [id]} groups]
               [group-list-item {:group-id id
                                 :on-click #(dispatch [:app.events/navigate-query {:group-id id}])
                                 :active   (= group-id id)}])))]]
        (when group-id
          [modified-transforms {:group-id    group-id
                                :on-edit     on-edit
                                :selectable? mergable?}])]

       (when mergable?
         [dialog-actions {:class "border-t px-5 py-4 mt-0 justify-between"}
          [:div {:class "flex items-center gap-2 font-medium"}
           [:> AlertCircle]
           [:p "We strongly encourage that Dev environments are regularly merged and then deleted."]]

          [:div {:class "flex gap-2 items-center"}
           (when (empty? selected-ids)
             [:span {:class "font-bold text-kleene-error-01"} "Select transforms to merge"])
           [button {:variant  :secondary
                    :disabled (empty? selected-ids)
                    :on-click merge-environment}
            "Merge"]]])])))

(defn close-diff-dialog []
  (dispatch [:app.events/navigate-query {:compare  nil
                                         :group-id nil}]))

(defn diffs-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        open? (:compare query)]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open
                                      (close-diff-dialog)))}
     [dialog-content {:size       :large
                      :no-padding true
                      :class      "flex flex-col overflow-hidden h-5/6"}
      [diffs-dialog-render {:group-id  (:group-id query)
                            :mergable? false
                            :on-close  close-diff-dialog}]]]))

(defn close-merge-dialog
  []
  (dispatch [:app.events/navigate-query {:merge    nil
                                         :group-id nil}]))

(defn merge-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        open? (:merge query)]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open
                                      (close-merge-dialog)))}
     [dialog-content {:size       :large
                      :no-padding true
                      :class      "flex flex-col overflow-hidden h-5/6"}
      [diffs-dialog-render {:group-id  (:group-id query)
                            :mergable? true
                            :on-close  close-merge-dialog}]]]))






(defn close-create-transform-modal []
  (dispatch [:sql-editor/update {:sql-editor/create-transform-drawer-open? false}]))

(defn open-create-transform-modal []
  (dispatch [:sql-editor/update {:sql-editor/create-transform-drawer-open? true}]))


(defn transform-create-fields []
  (dispatch [:groups-panel/add nil])
  (dispatch [:group.api/fetch-groups {:replace? true
                                      :items-per-page 1000}])

  (fn [{:keys [fields errors parsing-failed? dev-env?]}]
    (let [{:keys [ignore-parser group-id name description tags new-group]} fields
          prod-active? @(re-posh/subscribe [:environments.subs/production-active?])

          {:groups-panel/keys [groups]}
          @(re-posh/subscribe [:groups-panel/pull '[{:groups-panel/groups [:group/id :group/name]}]])

          groups         (sort-by (comp clojure.string/lower-case :group/name) groups)
          ignore-parser? (:value ignore-parser)
          disabled       dev-env?]

      [:<>
       [:div {:class "flex flex-col gap-4"}

        (when-not prod-active?
          [alert {:variant :warning
                  :icon    AlertTriangle
                  :title   "Some fields are disabled in development environment."}])

        [fields/input
         {:field         name
          :errors        errors
          :controlled?   false
          :id            :transform-name
          :data-cy       "transform-name-input"
          :label         "Transform name"}]

        ^{:key (str group-id errors groups)}
        [fields/select {:field    group-id
                        :errors   errors
                        :data-cy  "transform-group-input"
                        :id       :group-id
                        :label   "Add to group *"
                        :native  false
                        :options (into [{:value "new-group" :text "Create new group" :data-cy "transform-group-input-option"}
                                        {:type :separator}]
                                       (map (fn [{:group/keys [name id]}]
                                              {:value id :text name :data-cy "transform-group-input-option"})
                                            groups))}]

        (when (= (:value group-id) "new-group")
          [fields/input
           {:field         new-group
            :errors        errors
            :id            :new-group-name
            :label         "New group name *"}])

        [fields/input
         {:field         description
          :errors        errors
          :id            :transform-description
          :textarea      true
          :label         "Transform description"}]

        [tags-autocomplete
         {:tags      (:value tags)
          :on-change (:action tags)}]

        (when (some? (:form errors))
          [:span {:class "text-kleene-text-error"}
           (-> errors :form :value)])]

       (when parsing-failed?
         [:div {:class "mt-4"}
          [alert {:variant :warning
                  :icon    AlertTriangle
                  :title   "We failed to parse the SQL. You can ignore this error and save anyway, but we will not be able to identify transform's dependencies automatically."}]

          [:div {:class "mt-4"}
           [switch {:label     "Ignore parsing errors"
                    :checked   ignore-parser?
                    :disabled  disabled
                    :on-change #((:action ignore-parser) (not ignore-parser?))}]]])])))


(defn create-transform-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {:keys [group-id]} query

        open?     @(re-posh/subscribe [:sql-editor/prop :sql-editor/create-transform-drawer-open?])

        dev-env?  (not @(re-posh/subscribe [:environments.subs/production-active?]))
        group     @(re-posh/subscribe [:group/pull [:group/id group-id]])

        tab-idx   (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc       @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data  (nth tdc tab-idx {})
        metadata  (:metadata tab-data)
        code      (:code tab-data)

        output-tables (get-in metadata [:transform-parsed-tables :output-tables])
        input-tables (get-in metadata [:transform-parsed-tables :input-tables])
        parsing-error (seq (:transform-parsing-error metadata))

        parser-loading? @(re-posh/subscribe [:mold-http/loading? :transform.api/parse-sql-v2])


        loading? (or @(re-posh/subscribe [:mold-http/loading? :transform.api/parse-sql-v2])
                     @(re-posh/subscribe [:mold-http/loading? :transform.api/update-transform])
                     @(re-posh/subscribe [:mold-http/loading? :environments.api/update-environment-transform]))

        submit (fn [form-state]
                 (let [form-data (form-component/model->fields form-state)
                       group-id (:group-id form-data)
                       new-group? (= group-id "new-group")
                       transform (dissoc form-data :group-id)
                       create-transform #(create-and-edit % {:replace-tab-idx tab-idx
                                                             :transform       transform})]

                   (close-create-transform-modal)
                   (dispatch-sync [:sql-console.events/save-new-transform-template code])
                   (if new-group?
                     (dispatch [:group.api/create-group
                                {:group-name (:new-group form-data)}
                                {:on-success (fn [group-id]
                                               (create-transform group-id)
                                               (dispatch [:group.api/fetch-groups {:replace? true
                                                                                   :items-per-page 1000}]))}])
                     (create-transform group-id))))

        try-generating-name (fn []
                              (when (some? output-tables)
                                (c.string/join " " output-tables)))

        on-close  close-create-transform-modal]

    (react/useEffect
     (fn []

       (dispatch-sync [:group.events/search-groups])

       ;; return unsubscribe callback
       (fn []))
     #js [])


    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true}
       "Create transform"]
      [:div {:class "mt-4"}

       (if parser-loading?
         [:div {:class "m-6"}
          [c.spinner/spinner-lucide]]

         [form-component/form
          (->form-model
           {:id            :create-transform-form
            :track-changes true
            :validate      (partial form-component/validate-form ::new-transform-form)
            :on-submit     submit
            :fields        [{:name :ignore-parser :value false :process identity}
                            {:name :input-tables :value (or input-tables []) :process identity}
                            {:name :output-tables :value (or output-tables []) :process identity}
                            {:name :group-id :value nil :process identity}
                            {:name :new-group :value nil}
                            {:name :command :value (or code "") :process identity}
                            {:name :name :value (try-generating-name)}
                            {:name :description :value ""}
                            {:name :tags :value [] :process identity}]})
          (fn [fields errors on-submit]
            [:<>
             [transform-create-fields
              {:on-submit             on-submit
               :errors                errors
               :fields                fields
               :show-deps?            true
               :parsing-failed?       parsing-error
               :group-bulk-scheduled? (:group/bulk-scheduled group)
               :dev-env?              dev-env?}]

             [:footer {:class "mt-4 flex justify-end"}
              [button {:data-cy  "update-transform-btn"
                       :type "submit"
                       :disabled loading?
                       :on-click on-submit}
               "Save transform"]]])])]]]))


;; Webhooks
(defn create-webhook-or-trigger [form-state {:keys [webhooks on-success]}]
  (let [{:keys [transform-master-id event-type url method body query-params headers]}
        (form-component/model->fields form-state)
        params {:transform-master-id transform-master-id
                :event-type  event-type
                :trigger {:action-type :http-call
                          :params      {:url     url
                                        :body    body
                                        :query-params   (js/JSON.parse query-params)
                                        :method  method
                                        :headers (js/JSON.parse headers)}}
                :on-success  on-success}]
    (if-let [webhook (find-by-field webhooks :webhook/event-type event-type )]
      (dispatch [:webhooks.api/create-webhook-trigger (assoc params :hook-id (:webhook/hook-id webhook))])
      (dispatch [:webhooks.api/create-webhook params]))))

(defn create-trigger [action-type master-id]
  (dispatch [:webhooks.api/create-trigger {:action-type action-type
                                           :params      {:master-id master-id}}]))

(defn json-editor [{:keys [field readonly class]}]
  [:div {:class (clsx "h-[5lh]" class)}
   [:f> monaco.editor/editor
    {:language    "json"
     :theme       "vs-dark"
     :id          :headers
     :value       (:value field)
     :on-change   (fn [value]
                    ((:action field) value))
     :options     {:fontSize        14
                   :automaticLayout true
                   :lineNumbers     "off"
                   :readOnly        (or readonly false)}
     :controlled? true}]])

(defn trigger-row [{:keys [trigger]}]
  (let [{:webhook-trigger/keys [trigger-id client-id]} trigger
        [method set-method] (react/useState "GET")

        body (-> {:trigger-id trigger-id
                  :client-id  client-id}
                 clj->js
                 (js/JSON.stringify nil 2))
        url  (str API_HOST "/api/webhook" (when (= "GET" method) (str "/" client-id "/" trigger-id)))]
    [:div {:class "grid grid-cols-[1fr_auto] grid-rows-[auto_auto] gap-2 items-end"}
     [:div {:class "flex gap-2"}
      [select {:value     method
               :on-change set-method
               :label     "HTTP method"
               :native    false
               :class     "w-28"
               :options   [{:value "GET"} {:value "POST"}]}]
      [:div {:class "flex flex-col gap-2 w-full"}
       [:div {:class "relative flex-1"}
        [input {:label    "Trigger URL"
                :value     url
                :on-change (fn [])
                :read-only true
                :class    "truncate"}]
        [button {:on-click #(dispatch [:app.events/copy-to-clipboard url true])
                 :size     :micro
                 :variant  :empty
                 :icon     Copy
                 :class    "absolute right-0 top-0"}
         "Copy"]]]]
     [tooltip {:content "Delete trigger"
               :trigger-as-child false}
      [button {:on-click #(dispatch [:webhooks.api/delete-trigger {:trigger-id trigger-id}])
               :variant  :error
               :size     :icon-sl
               :icon     Trash}]]
     (when (= "POST" method)
       [:div {:class "relative"}
        [typography/h4 "Request details"]
        [tabs/tabs {:value   :body
                    :variant :underlined
                    :tabs    [{:label   "Body"
                               :value   :body
                               :content [:<>
                                         [json-editor {:field    {:value body}
                                                       :class    "h-[4lh]"
                                                       :readonly true}]
                                         [button {:size     :micro
                                                  :variant  :empty
                                                  :icon     Copy
                                                  :class    "absolute top-2 right-0"
                                                  :on-click #(dispatch [:app.events/copy-to-clipboard body true])}
                                          "Copy"]]}]}]])]))

(defn request-details-fields [{:keys [fields]}]
  (let [[tab set-tab] (react/useState :headers)]
    [:div
     [typography/h4 "Request details"]
     [:div {:class "relative"}
      [chip {:class "absolute top-2 right-2"} "JSON"]
      [tabs/tabs {:value   tab
                  :on-value-change set-tab
                  :variant :underlined
                  :tabs    [{:label   "Headers"
                             :value   :headers
                             :content [json-editor {:field (get fields :headers)}]}
                            {:label   "Query"
                             :value   :query-params
                             :content [json-editor {:field (get fields :query-params)}]}
                            {:label   "Body"
                             :value   :body
                             :content [json-editor {:field (get fields :body)}]}]}]]]))

(defn new-webhook-row [{:keys [master-id webhooks on-close]}]
  [:div {:class "p-2 bg-kleene-primary-05"}
   [form-component/form
    (->form-model
     {:id        :create-webhook-form
      :on-submit #(create-webhook-or-trigger % {:webhooks   webhooks
                                                :on-success on-close})
      :clean-up  #(dispatch [:form-model/delete [:form-model/id :create-inbound-webhook-form]])
      :fields    [{:name    :event-type
                   :value   "success"
                   :process identity}
                  {:name    :url
                   :value   ""
                   :process #(jsi/get-in % [:target :value])}
                  {:name    :body
                   :value   "{}"
                   :process identity}
                  {:name    :headers
                   :value   "{\n  \"content-type\": \"application/json\"\n}"
                   :process identity}
                  {:name    :query-params
                   :value   "{}"
                   :process identity}
                  {:name    :method
                   :value   "GET"
                   :process identity}
                  {:name :transform-master-id :value master-id}]})
    (fn [fields errors on-submit]
      [:div {:class "flex flex-col gap-2"}
       [typography/h3 "New webhook form"]
       [:div {:class "flex gap-2 my-2 items-end"}
        [select {:id      :event-type
                 :field   (get fields :event-type)
                 :label   "Transform event"
                 :native  false
                 :errors  errors
                 :class  "w-56"
                 :required true
                 :options [{:value "success" :text "Success"}
                           {:value "fail" :text "Fail"}
                           {:value "pending" :text "Pending"}]}]
        [select {:id      :method
                 :field   (get fields :method)
                 :label   "HTTP Method"
                 :native  false
                 :errors  errors
                 :class   "w-28 shrink-0"
                 :required true
                 :options [{:value "GET"} {:value "POST"}]}]
        [input {:name     :url
                :field    (get fields :url)
                :label    "Destination URL"
                :errors   errors
                :placeholder "https://yourdomain.com/webhook-handler"
                :required true}]]
       [:f> request-details-fields {:fields fields}]
       [:div {:class "flex gap-2 justify-end"}
        [button {:variant :empty :on-click on-close}
         "Cancel"]
        [button {:on-click on-submit} "Create"]]])]])

(defn webhook-trigger-row [{{:webhook/keys [hook-id event-type triggers]} :webhook
                            {:keys [trigger-id params]} :trigger}]
  (let [[tab set-tab] (react/useState :headers)
        [expanded set-expanded] (react/useState false)

        {:keys [method headers query body url]} params]
    [:div {:class "grid grid-cols-[1fr_auto] grid-rows-[auto_auto] gap-2 items-end"}
     [:div {:class "flex gap-2 items-end"}
      [select {:id      :event-type
               :value   event-type
               :label   "Transform event"
               :on-change #()
               :disabled true
               :class  "w-32 shrink-0"
               :options [{:value "success" :text "Success"}
                         {:value "fail" :text "Fail"}
                         {:value "pending" :text "Pending"}]}]
      [select {:id      :method
               :value   method
               :on-change #()
               :label   "HTTP Method"
               :class   "w-28 shrink-0"
               :disabled true
               :options [{:value "GET"} {:value "POST"}]}]
      [:div {:class "relative flex-1"}
       [button {:on-click #(dispatch [:app.events/copy-to-clipboard url true])
                :size     :micro
                :variant  :empty
                :icon     Copy
                :class    "absolute right-0 top-0"}
        "Copy"]
       [input {:name     :url
               :label    "Destination URL"
               :value    url
               :on-change #()
               :read-only true
               :disabled  true}]]]
     [:div {:class "flex gap-2"}
      [button {:variant  :empty
               :size     :icon-sl
               :icon     ChevronDown
               :class    (when expanded "rotate-180")
               :on-click #(set-expanded not)}]
      [button {:variant  :error
               :size     :icon-sl
               :icon     Trash
               :on-click #(if (> (count triggers) 1)
                            (dispatch [:webhooks.api/delete-webhook-trigger {:hook-id hook-id
                                                                             :trigger-id trigger-id}])
                            (dispatch [:webhooks.api/delete-webhook {:hook-id hook-id}]))}]]
     (when expanded
       [:div
        [typography/h4 "Request details"]
        [:div {:class "relative"}
         [chip {:class "absolute top-2 right-2"} "JSON"]
         [tabs/tabs {:value   tab
                     :on-value-change set-tab
                     :variant :underlined
                     :tabs    [{:label   "Headers"
                                :value   :headers
                                :content [json-editor {:field    {:value (js/JSON.stringify (clj->js headers) nil 2)}
                                                       :readonly true}]}
                               {:label   "Query"
                                :value   :query
                                :content [json-editor {:field    {:value (js/JSON.stringify (clj->js query) nil 2)}
                                                       :readonly true}]}
                               {:label   "Body"
                                :value   :body
                                :content [json-editor {:field    {:value (js/JSON.stringify (clj->js body) nil 2)}
                                                       :readonly true}]}]}]]])]))

(defn transform-webhooks' [{:keys [master-id triggers webhooks]}]
  (let [[show-new-webhook set-show-new-webhook] (react/useState false)]
    (react/useEffect
     (fn []
       (dispatch [:webhooks.api/fetch-triggers])
       (dispatch [:webhooks.api/fetch-transform-webhooks {:master-id master-id}])
       js/undefined)
     #js [master-id])

    [:div {:class "flex flex-col gap-4"}
     [:div
      [:header {:class "flex gap-2 items-center"}
       [typography/h3 "Inbound triggers"]
       [tooltip {:content "Use inbound trigger endpoint to start a transform execution."}
        [:> Info {:size 20}]]]

      [:div {:class "flex flex-col gap-4 my-2"}
       (if (empty? triggers)
         [:p "No inbound triggers found."]
         (for [{:webhook-trigger/keys [trigger-id] :as t} triggers]
           ^{:key trigger-id}
           [:f> trigger-row {:trigger t}]))]

      [:div {:class "mt-2"}
       [button {:on-click #(create-trigger :run-transform master-id)
                :size     :small}
        "Create inbound trigger"]]]

     [:div {:class "border-t h-0"}]

     [:div
      [:header {:class "flex gap-2 items-center"}
       [typography/h3 "Outbound triggers"]
       [tooltip {:content "Create outbound trigger to execute request after transform execution."}
        [:> Info {:size 20}]]]

      [:div {:class "flex flex-col gap-4 my-2"}
       (if (empty? webhooks)
         [:p "No outbound triggers found."]
         (for [{:webhook/keys [hook-id triggers] :as w} webhooks]
           ^{:key hook-id}
           [:<>
            (for [{:keys [trigger-id] :as trigger} triggers]
              ^{:key trigger-id}
              [:f> webhook-trigger-row {:trigger trigger
                                        :webhook w}])]))]

      (when show-new-webhook
        [:f> new-webhook-row {:master-id master-id
                              :webhooks  webhooks
                              :on-close  #(set-show-new-webhook false)}])

      (when-not show-new-webhook
        [:div {:class "mt-4"}
         [button {:on-click #(set-show-new-webhook true)
                  :size     :small}
          "Create outbound trigger"]])]]))


(defn save-notification-settings [master-id form-state]
  (let [{:keys [slack-webhook ms-teams-webhook email use-account-slack use-account-ms-teams]} (form-component/model->fields form-state)]
    (dispatch [:notifications.api/save-notifications
               {:master-id            master-id
                :source-type          "TRANSFORM"
                :slack-webhook        slack-webhook
                :ms-teams-webhook     ms-teams-webhook
                :email                email
                :use-account-slack    use-account-slack
                :use-account-ms-teams use-account-ms-teams
                :on-success           [:app.events/navigate-query {:notifications nil :group-id nil :master-id nil}]}])))


(defn transform-notifications' [{:keys [master-id notification account-settings]}]
  (react/useEffect
   (fn []
     (dispatch [:notifications.api/fetch-notification {:master-id master-id}])
     js/undefined)
   #js [master-id])

  [:div {:class "flex flex-col gap-4"}
   [:div
    [:header {:class "flex gap-2 items-center"}
     [typography/h3 "Notify when transform execution state changes"]
     [tooltip {:content "You will receive a notification when the latest transform execution status is different from the previous one."}
      [:> Info {:size 20}]]]]

   [:div
    [form-component/form
     (->form-model
      {:id        :transform-notifications-form
       :on-submit (partial save-notification-settings master-id)
       :clean-up  #(dispatch [:form-model/delete [:form-model/id :transform-notifications-form]])
       :fields    [{:name  :slack-webhook
                    :value (get notification :slack-webhook "")}
                   {:name  :ms-teams-webhook
                    :value (get notification :ms-teams-webhook "")}
                   {:name  :email
                    :value (get notification :email "")}
                   {:name    :use-account-slack
                    :value   (or (get notification :use-account-slack) false)
                    :process identity}
                   {:name    :use-account-ms-teams
                    :value   (or (get notification :use-account-ms-teams) false)
                    :process identity}]})
     (fn [fields errors on-submit]
       (let [{:keys [use-account-slack use-account-ms-teams]} fields
             use-account-slack?    (:value use-account-slack)
             use-account-ms-teams? (:value use-account-ms-teams)]
         [:<>
          [:div {:class "my-2"}
           [tabs/tabs-root {:default-value "slack"}
            [tabs/tabs-list {:class "mb-4"}
             [tabs/tabs-trigger {:value "slack"} "Slack"]
             [tabs/tabs-trigger {:value "ms-teams"} "MS Teams"]
             [tabs/tabs-trigger {:value "email"} "Email"]]

            [tabs/tabs-content {:value "slack"}
             [:div
              (when (:account-notifications/slack-preference account-settings)
                [switch {:id      :use-account-slack
                         :label   "Use account webhook"
                         :field   use-account-slack
                         :classes {:root "mb-4"}}])
              (if use-account-slack?
                [:a {:href   "/admin?tab=notification-preferences"
                     :class  "underline text-kleene-secondary"
                     :target :_blank}
                 "You can check your account settings here."]
                [input {:name     :slack-webhook
                        :field    (:slack-webhook fields)
                        :label    "Slack webhook URL"
                        :errors   errors
                        :class    "flex-1"}])]]

            [tabs/tabs-content {:value "ms-teams"}
             [:div
              (when (:account-notifications/ms-teams-preference account-settings)
                [switch {:id      :use-account-ms-teams
                         :label   "Use account webhook"
                         :field   use-account-ms-teams
                         :classes {:root "mb-4"}}])
              (if use-account-ms-teams?
                [:a {:href   "/admin?tab=notification-preferences"
                     :class  "underline text-kleene-secondary"
                     :target :_blank}
                 "You can check your account settings here."]
                [input {:name     :ms-teams-webhook
                        :field    (:ms-teams-webhook fields)
                        :label    "MS Teams webhook URL"
                        :errors   errors
                        :class    "flex-1"}])]]

            [tabs/tabs-content {:value "email"}
             [input {:name   :email
                     :field  (:email fields)
                     :label  "Email address"
                     :helper "Enter a single email address or a comma-separated list of email addresses."
                     :errors errors}]]]]

          [:div {:class "mt-4 flex justify-end"}
           [button {:on-click on-submit
                    :size     :small}
            "Save notification settings"]]]))]]])


(defn transform-webhooks [{:keys [master-id] :as props}]
  (when master-id
    (let [triggers @(re-posh/subscribe [:webhooks.subs/transform-triggers master-id])
          webhooks @(re-posh/subscribe [:webhooks.subs/transform-webhooks master-id])]
      [:f> transform-webhooks' (assoc props
                                      :triggers triggers
                                      :webhooks webhooks)])))


(defn transform-notifications [{:keys [master-id] :as props}]
  (when master-id
    (let [notification     @(re-posh/subscribe [:notifications.subs/job-notification master-id])
          account-settings @(re-posh/subscribe [:account-notifications/pull])]
      [:f> transform-notifications' (assoc props :notification notification
                                           :account-settings account-settings)])))


(defn webhooks-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {:keys [master-id group-id]} query
        open? (:webhooks query)

        on-close  (fn []
                    (dispatch [:app.events/navigate-query {:webhooks nil
                                                           :group-id         nil
                                                           :master-id     nil}]))]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true} "Webhooks"]

      [transform-webhooks {:master-id master-id
                           :group-id group-id}]]]))


(defn notifications-dialog [{:keys [route]}]
  (let [{:app-route/keys [query]} route
        {:keys [master-id]} query
        open?    (:notifications query)
        on-close #(dispatch [:app.events/navigate-query
                             {:notifications nil
                              :group-id      nil
                              :master-id     nil}])]
    [dialog-root {:open           open?
                  :on-open-change (fn [open]
                                    (when-not open (on-close)))}
     [dialog-content {:size :medium}
      [dialog-title {:truncate true} "Notifications"]

      [transform-notifications {:master-id master-id}]]]))


(defn modals []
  (let [route @(re-posh/subscribe [:app-route/pull])]
    [:<>
     [search-modal {:route route}]

     [group-create-and-move-dialog {:route route}]
     [group-edit-dialog {:route route}]
     [group-delete-dialog {:route route}]

     [transform-delete-dialog {:route route}]
     [transform-settings-dialog {:route route}]
     [transform-clone-dialog {:route route}]

     [environment-create-dialog {:route route}]
     [environment-delete-dialog {:route route}]

     [:f> create-transform-dialog {:route route}]

     [restore-dialog {:route route}]
     [webhooks-dialog {:route route}]
     [notifications-dialog {:route route}]

     [diffs-dialog {:route route}]
     [merge-dialog {:route route}]]))
